import { Alert, Box, Divider, Stack, Typography } from '@mui/material';
import { GlowCard, StatusPill } from '@norvix/ui';

import type { CurrencyPosition } from '../../api/commission-transfers.api.js';

import { STATUS_LABEL, centsLabel, dateTimeUtc, voidedAfterTransferNotice } from './transfer-format.js';

interface RowProps { label: string; value: string; strong?: boolean; color?: string | undefined }

function Row({ label, value, strong = false, color }: RowProps): JSX.Element {
  return <Stack direction="row" justifyContent="space-between" gap={2}>
    <Typography variant="body2" color="text.secondary">{label}</Typography>
    <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: strong ? 700 : 400, color, textAlign: 'right', overflowWrap: 'anywhere' }}>{value}</Typography>
  </Stack>;
}

/** What the next run moves: the pending commissions, capped at Norvix's money on the account (DEV-127). */
export function nextSweep(position: CurrencyPosition): { moveCents: bigint; feesCents: bigint } | null {
  if (position.norvixShareCents === undefined || position.norvixShareCents === null) return null;
  const pending = BigInt(position.pendingCommissionsCents);
  const share = BigInt(position.norvixShareCents);
  const moveCents = share <= 0n ? 0n : pending < share ? pending : share;
  return { moveCents, feesCents: pending - moveCents };
}

function PositionCard({ position }: { position: CurrencyPosition }): JSX.Element {
  const c = position.currency;
  const difference = position.differenceCents === null ? null : BigInt(position.differenceCents);
  const sweep = nextSweep(position);
  const voidedNotice = voidedAfterTransferNotice(position.voidedAfterTransfer, c);
  return <GlowCard style={{ padding: 20, borderRadius: 12 }}>
    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }} gap={1} flexWrap="wrap">
      <Typography variant="h6">{c} client money</Typography>
      {position.covered !== null && <StatusPill tone={position.covered ? 'success' : 'error'}>
        {position.covered ? 'Customers covered' : 'Shortfall'}</StatusPill>}
    </Stack>
    {position.positionError !== null && <Alert severity="warning" sx={{ mb: 1.5 }}>Live balance unavailable: {position.positionError}</Alert>}
    {voidedNotice !== null && <Alert severity="error" sx={{ mb: 1.5 }}>{voidedNotice}</Alert>}
    <Stack spacing={0.5} sx={{ mb: 1.5 }}>
      <Row label="From: client-money account" value={position.fromIban ?? 'unknown'} />
      <Row label="To: Norvix revenue account" value={position.toIban ?? 'not set'} color={position.toIban ? undefined : 'error.main'} />
    </Stack>
    <Stack spacing={0.75}>
      <Row label="Balance at GuruPay (live)" value={centsLabel(position.masterBalanceCents, c)} strong />
      <Row label="Owed to customers" value={centsLabel(position.liabilitiesCents, c)} />
      <Row label="  of which payouts in flight" value={centsLabel(position.liabilitiesInFlightCents, c)} />
      {position.liabilitiesInboundCents !== undefined ? (
        <Row label="  of which incoming, not yet credited" value={centsLabel(position.liabilitiesInboundCents, c)} />
      ) : null}
      {position.norvixShareCents !== undefined ? (
        <Row label="Norvix's money on the account (most a sweep moves)" value={centsLabel(position.norvixShareCents, c)} />
      ) : null}
      <Row label="Commissions pending transfer" value={centsLabel(position.pendingCommissionsCents, c)} />
      <Row label="Commissions in transfer" value={centsLabel(position.inTransferCents, c)} />
      <Divider sx={{ my: 0.5 }} />
      {sweep !== null && <>
        <Row label="Next run moves to the revenue account" value={centsLabel(sweep.moveCents.toString(), c)} strong />
        <Row label="Commissions already taken by GuruPay fees" value={centsLabel(sweep.feesCents.toString(), c)} />
      </>}
      {difference !== null && difference > 0n &&
        <Row label="Unexplained extra money on the account" value={centsLabel(position.differenceCents, c)} strong color="warning.main" />}
    </Stack>
    <Box sx={{ mt: 1.5 }}>
      <Typography variant="caption" color="text.secondary" display="block">
        Last transfer: {position.lastTransfer === null ? 'none yet'
          : `${dateTimeUtc(position.lastTransfer.createdAt)} · ${centsLabel(position.lastTransfer.amountCents, c)} · ${STATUS_LABEL[position.lastTransfer.status]}`}
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
        Next scheduled run: {position.nextTransferAt === null ? 'off' : dateTimeUtc(position.nextTransferAt)}
      </Typography>
    </Box>
  </GlowCard>;
}

export function ClientMoneyPositionCards({ currencies }: { currencies: CurrencyPosition[] }): JSX.Element {
  if (currencies.length === 0) {
    return <Alert severity="info">No currency has both a client-money and a revenue account configured. Set them in System Settings → GuruPay.</Alert>;
  }
  return <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0,1fr)', md: 'repeat(2,minmax(0,1fr))', xl: 'repeat(3,minmax(0,1fr))' }, gap: 2 }}>
    {currencies.map((position) => <PositionCard key={position.currency} position={position} />)}
  </Box>;
}
